import { EXPERT_KNOBS } from '../bot/levels.ts';
import { profileKnobsFrom } from './document.ts';
import { clampBotKnob } from './limits.ts';
import {
  combinedPace,
  DISTANCE_SENSE_PACE,
  estimatedPace,
  limitForPace,
  limitPace,
  MAX_DIAL_AIM_M,
  MIN_DIAL_SPEED_LIMIT_KMH,
  slipPace,
  slipsForPace,
  STEADY_SLIPS_MAX,
  steadyPace,
} from './paceModel.ts';
import type { BotProfileKnobs } from './types.ts';

/**
 * The bot workshop's simple controls: four 0–100 bars a player drags instead of setting knobs.
 * Each bar owns its own knobs, so dragging one never moves another:
 * - **Skill** is the pace it aims for: 100 is Expert, 60 is 140% of Expert's time.
 * - **Consistency** is how it loses that time: high, steadily (slips, distance-only course
 *   sense, and past those a speed limit); low, by aim error, which is what crashes and makes one
 *   race unlike the next.
 * - **Aggression** sets squeezing, squeeze tries, and the will to block.
 * - **Manners** sets reading rivals and following distance, and holds blocking back.
 *
 * Skill and Consistency are solved against the measured pace model (`paceModel.ts`), so the pace
 * a bar asks for is the pace a playtest should see, give or take the noise of a few races.
 */
export type BotDials = {
  skill: number;
  consistency: number;
  aggression: number;
  manners: number;
};

export const BOT_DIAL_KEYS = ['skill', 'consistency', 'aggression', 'manners'] as const satisfies readonly (keyof BotDials)[];

/** Where Expert sits: full skill, no aim error, squeezes on, no blocking, no extra caution. */
export const EXPERT_DIALS: Readonly<BotDials> = { skill: 100, consistency: 100, aggression: 50, manners: 25 };

/** Rival turns one own turn is worth giving up to block, at full Aggression and no Manners. */
const MAX_BLOCKING = 1;
/** The steadiest a bot can lose time: time sense and the most slips, or distance-only and the most. */
const STEADY_TIME_MAX = slipPace(STEADY_SLIPS_MAX);
const STEADY_SENSE_MAX = DISTANCE_SENSE_PACE * STEADY_TIME_MAX;
/** Past those, a speed limit alone, which reaches as far as the bars go and never crashes. */
const STEADY_MAX = limitPace(MIN_DIAL_SPEED_LIMIT_KMH);

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));
const toDial = (share: number) => Math.round(clamp01(share) * 100);

/**
 * The lowest Skill the bars reach: the most aim error they use, plus slips and distance-only
 * sense. The speed limit could go slower, but then low Consistency would have no room.
 */
export const MIN_SKILL_DIAL = Math.max(0, Math.ceil(100 * (2 - combinedPace(STEADY_SENSE_MAX, MAX_DIAL_AIM_M))));

function targetPace(skill: number): number {
  return 2 - Math.min(100, Math.max(MIN_SKILL_DIAL, skill)) / 100;
}

/** The aim error (m) that, with this steady part, sails at `pace`. The model rises with aim. */
function aimFor(pace: number, steady: number): number {
  let low = 0;
  let high = MAX_DIAL_AIM_M;

  if (combinedPace(steady, low) >= pace) {
    return 0;
  }

  for (let step = 0; step < 40; step += 1) {
    const mid = (low + high) / 2;

    if (combinedPace(steady, mid) < pace) {
      low = mid;
    } else {
      high = mid;
    }
  }

  return high;
}

function consistencyOfAim(aimErrorM: number): number {
  return toDial(1 - aimErrorM / MAX_DIAL_AIM_M);
}

/**
 * The Consistency a Skill leaves room for, as 0–100. At full skill there is no time to lose, so
 * it is pinned at 100. Lower, the top stays at 100 (a speed limit loses any time steadily); the
 * bottom is all of it from aim error.
 */
export function consistencyRange(skill: number): readonly [number, number] {
  const pace = targetPace(skill);
  return [consistencyOfAim(aimFor(pace, 1)), consistencyOfAim(aimFor(pace, STEADY_MAX))];
}

/** Each dial inside what it can reach. */
function clampDials(dials: BotDials): BotDials {
  const skill = Math.round(Math.min(100, Math.max(MIN_SKILL_DIAL, dials.skill)));
  const [low, high] = consistencyRange(skill);

  return {
    skill,
    consistency: Math.round(Math.min(high, Math.max(low, dials.consistency))),
    aggression: Math.round(Math.min(100, Math.max(0, dials.aggression))),
    manners: Math.round(Math.min(100, Math.max(0, dials.manners))),
  };
}

type SteadyKnobs = { courseSense: 'time' | 'distance'; temperature: number; speedLimitKmh: number };

/**
 * The steady knobs for a steady part: slips alone, then distance-only sense plus slips, then a
 * speed limit alone. Past about Skill 86 slips and distance-only sense run out; the limit was
 * measured on its own, so it replaces them rather than stacking on them.
 */
function steadyKnobs(steady: number): SteadyKnobs {
  if (steady > STEADY_SENSE_MAX) {
    return { courseSense: 'time', temperature: 0, speedLimitKmh: limitForPace(steady) };
  }

  return { ...senseKnobs(steady), speedLimitKmh: 0 };
}

function senseKnobs(steady: number): Omit<SteadyKnobs, 'speedLimitKmh'> {
  if (steady <= STEADY_TIME_MAX) {
    return { courseSense: 'time', temperature: slipsForPace(steady) };
  }

  // Distance-only sense is a lump of about a tenth: below it, take the nearer end.
  if (steady < DISTANCE_SENSE_PACE) {
    return steady - STEADY_TIME_MAX < DISTANCE_SENSE_PACE - steady
      ? { courseSense: 'time', temperature: STEADY_SLIPS_MAX }
      : { courseSense: 'distance', temperature: 0 };
  }

  return { courseSense: 'distance', temperature: slipsForPace(steady / DISTANCE_SENSE_PACE) };
}

/** Every knob from the four dials. Foresight and search are Expert's; brakes are true. */
export function knobsFromDials(dials: BotDials): BotProfileKnobs {
  const clamped = clampDials(dials);
  const pace = targetPace(clamped.skill);
  const wantedAim = MAX_DIAL_AIM_M * (1 - clamped.consistency / 100);
  // The steady part the wanted aim leaves to make up, solved from the combined model.
  const weight = 1 - 0.5 * Math.min(1, wantedAim / 3);
  const steadyWanted = Math.min(STEADY_MAX, Math.max(1, 1 + (pace - combinedPace(1, wantedAim)) / weight));
  const steady = steadyKnobs(steadyWanted);
  const temperature = clampBotKnob('temperature', steady.temperature);
  const speedLimitKmh = clampBotKnob('speedLimitKmh', steady.speedLimitKmh);
  // Whatever the steady knobs really give, aim error makes up the rest of the pace. A speed
  // limit is not in steps, so it already leaves exactly the wanted aim error.
  const aim =
    speedLimitKmh > 0
      ? wantedAim
      : aimFor(
          pace,
          steadyPace({ courseSense: steady.courseSense, depth: EXPERT_KNOBS.depth, temperature, speedLimitKmh }),
        );
  const a = clamped.aggression / 100;
  const m = clamped.manners / 100;
  const nudges = a >= 0.15;

  return {
    ...profileKnobsFrom(EXPERT_KNOBS),
    courseSense: steady.courseSense,
    temperature,
    speedLimitKmh,
    aimErrorM: clampBotKnob('aimErrorM', aim),
    nudges,
    contactRoots: nudges ? clampBotKnob('contactRoots', 8 * clamp01((a - 0.15) / 0.35)) : 0,
    aggression: clampBotKnob('aggression', MAX_BLOCKING * clamp01((a - 0.5) / 0.5) * (1 - m)),
    forecastHulls: m >= 0.5,
    followDistance: m >= 0.75,
  };
}

/**
 * Dials as they take effect: Skill within reach, and Consistency as the aim error it really got
 * (the steady knobs come in steps, so aim makes up the difference).
 */
export function effectiveDials(dials: BotDials): BotDials {
  const clamped = clampDials(dials);
  return { ...clamped, consistency: consistencyOfAim(knobsFromDials(clamped).aimErrorM) };
}

/** Manners as a share, read back from the flags (the middle of each step). */
function mannersOf(knobs: BotProfileKnobs): number {
  if (knobs.followDistance) {
    return knobs.forecastHulls ? 0.875 : 0.75;
  }

  return knobs.forecastHulls ? 0.625 : 0.25;
}

/**
 * The dials a set of knobs comes closest to, for a profile edited knob by knob (or saved before
 * dials existed). Exact for Skill and Consistency up to rounding; Aggression and Manners land in
 * the middle of the step their flags imply.
 */
export function dialsFromKnobs(knobs: BotProfileKnobs): BotDials {
  const m = mannersOf(knobs);
  let a: number;

  if (knobs.aggression > 0) {
    a = 0.5 + 0.5 * clamp01(knobs.aggression / (MAX_BLOCKING * Math.max(0.25, 1 - m)));
  } else if (knobs.nudges) {
    a = Math.min(0.5, 0.15 + (0.35 * knobs.contactRoots) / 8);
  } else {
    a = 0.075;
  }

  return {
    skill: Math.max(0, Math.min(100, Math.round(100 * (2 - estimatedPace(knobs))))),
    consistency: consistencyOfAim(knobs.aimErrorM),
    aggression: toDial(a),
    manners: toDial(m),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Dials from a file, or null when any is missing or not a number. */
export function coerceBotDials(value: unknown): BotDials | null {
  if (!isRecord(value)) {
    return null;
  }

  const out: Partial<BotDials> = {};

  for (const key of BOT_DIAL_KEYS) {
    const raw = value[key];

    if (typeof raw !== 'number' || !Number.isFinite(raw)) {
      return null;
    }

    out[key] = Math.round(Math.min(100, Math.max(0, raw)));
  }

  return out as BotDials;
}
